class SubcollectionFacets {
  constructor(root) {
    this.root = root;
    this.config = JSON.parse(root.querySelector('[data-subcollection-facets-config]').textContent);
    this.params = new URLSearchParams(window.location.search);
    this.filters = [];
    this.cardCache = new Map();
    this.requestSequence = 0;
    this.priceTimer = null;

    root.querySelectorAll('[data-subcollection-product-handle]').forEach((card) => {
      this.cardCache.set(card.dataset.subcollectionProductHandle, card.outerHTML);
    });

    this.icons = {};
    root.querySelectorAll('template[data-subcollection-icon]').forEach((template) => {
      this.icons[template.dataset.subcollectionIcon] = template.innerHTML;
    });

    root.addEventListener('change', this.onChange.bind(this));
    root.addEventListener('input', this.onInput.bind(this));
    root.addEventListener('click', this.onClick.bind(this));
    window.addEventListener('popstate', () => {
      this.params = new URLSearchParams(window.location.search);
      this.load({ updateHistory: false });
    });

    this.load({ updateHistory: false });
  }

  async load({ updateHistory = true, replaceHistory = false, scrollToGrid = false } = {}) {
    const sequence = ++this.requestSequence;
    this.setLoading(true);

    try {
      const result = await this.fetchCollection();
      if (sequence !== this.requestSequence) return;

      this.filters = result.filters;
      const totalProducts = result.handles.length;
      const totalPages = Math.max(1, Math.ceil(totalProducts / this.config.productsPerPage));
      const requestedPage = Math.max(1, Number.parseInt(this.params.get('page') || '1', 10) || 1);
      const currentPage = Math.min(requestedPage, totalPages);

      if (currentPage === 1) {
        this.params.delete('page');
      } else if (currentPage !== requestedPage) {
        this.params.set('page', String(currentPage));
      }

      const start = (currentPage - 1) * this.config.productsPerPage;
      const visibleHandles = result.handles.slice(start, start + this.config.productsPerPage);

      await this.renderProducts(visibleHandles);
      if (sequence !== this.requestSequence) return;

      this.renderFilters();
      this.renderActiveFacets();
      this.renderProductCount(totalProducts);
      this.renderPagination(totalPages, currentPage);
      this.syncSortSelects();

      if (updateHistory) {
        this.updateHistory(replaceHistory);
      }

      if (scrollToGrid) {
        this.root.querySelector('#ProductGridContainer')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } catch (error) {
      if (sequence === this.requestSequence) {
        console.error('Unable to update subcollection filters.', error);
      }
    } finally {
      if (sequence === this.requestSequence) {
        this.setLoading(false);
      }
    }
  }

  async fetchCollection() {
    const filterInputs = this.buildProductFilters();
    const sort = this.getSortConfiguration();
    const handles = [];
    let filters = [];
    let after = null;
    let hasNextPage = true;
    let pageRequests = 0;

    while (hasNextPage && pageRequests < 20) {
      const response = await fetch('/api/2026-07/graphql.json', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: SubcollectionFacets.query,
          variables: {
            id: this.config.id,
            filters: filterInputs,
            sortKey: sort.sortKey,
            reverse: sort.reverse,
            after,
            country: this.config.country,
            language: this.config.language,
          },
        }),
      });

      if (!response.ok) {
        throw new Error(`Storefront API returned ${response.status}`);
      }

      const payload = await response.json();
      if (payload.errors?.length) {
        throw new Error(payload.errors.map((error) => error.message).join(', '));
      }

      const products = payload.data?.collection?.products;
      if (!products) {
        throw new Error(`Subcollection ${this.config.handle} is unavailable`);
      }

      handles.push(...products.nodes.map((product) => product.handle));
      if (pageRequests === 0) filters = products.filters || [];
      hasNextPage = products.pageInfo.hasNextPage;
      after = products.pageInfo.endCursor;
      pageRequests += 1;
    }

    return { handles, filters };
  }

  buildProductFilters() {
    const filters = [];
    let minimumPrice = null;
    let maximumPrice = null;

    this.params.forEach((value, key) => {
      if (key === 'filter.v.price.gte') {
        minimumPrice = Number.parseFloat(value);
        return;
      }
      if (key === 'filter.v.price.lte') {
        maximumPrice = Number.parseFloat(value);
        return;
      }
      if (!key.startsWith('filter.')) return;

      if (key === 'filter.v.availability') {
        filters.push({ available: value === '1' || value === 'true' });
      } else if (key.startsWith('filter.v.option.')) {
        filters.push({
          variantOption: {
            name: key.replace('filter.v.option.', ''),
            value,
          },
        });
      } else if (key === 'filter.p.vendor') {
        filters.push({ productVendor: value });
      } else if (key === 'filter.p.product_type') {
        filters.push({ productType: value });
      } else if (key === 'filter.p.tag') {
        filters.push({ tag: value });
      } else if (key.startsWith('filter.p.m.')) {
        const [, , , namespace, ...keyParts] = key.split('.');
        filters.push({
          productMetafield: {
            namespace,
            key: keyParts.join('.'),
            value,
          },
        });
      } else if (key.startsWith('filter.v.m.')) {
        const [, , , namespace, ...keyParts] = key.split('.');
        filters.push({
          variantMetafield: {
            namespace,
            key: keyParts.join('.'),
            value,
          },
        });
      }
    });

    if (Number.isFinite(minimumPrice) || Number.isFinite(maximumPrice)) {
      const price = {};
      if (Number.isFinite(minimumPrice)) price.min = minimumPrice;
      if (Number.isFinite(maximumPrice)) price.max = maximumPrice;
      filters.push({ price });
    }

    return filters;
  }

  getSortConfiguration() {
    const sortBy = this.params.get('sort_by') || this.config.defaultSort || 'manual';
    const mapping = {
      manual: ['MANUAL', false],
      'best-selling': ['BEST_SELLING', false],
      'title-ascending': ['TITLE', false],
      'title-descending': ['TITLE', true],
      'price-ascending': ['PRICE', false],
      'price-descending': ['PRICE', true],
      'created-ascending': ['CREATED', false],
      'created-descending': ['CREATED', true],
    };
    const [sortKey, reverse] = mapping[sortBy] || ['COLLECTION_DEFAULT', false];
    return { sortKey, reverse };
  }

  renderFilters() {
    if (!this.config.enableFiltering) return;

    const desktopWrapper = this.root.querySelector('#FacetsWrapperDesktop');
    if (desktopWrapper) {
      desktopWrapper.querySelectorAll('[data-subcollection-generated-filter]').forEach((element) => element.remove());
      const noScript = desktopWrapper.querySelector('noscript');
      const desktopMarkup = this.filters
        .map((filter, index) => this.renderDesktopFilter(filter, index))
        .join('');
      noScript?.insertAdjacentHTML('beforebegin', desktopMarkup);
    }

    const mobileMain = this.root.querySelector('.mobile-facets__main');
    if (mobileMain) {
      mobileMain.querySelectorAll('[data-subcollection-generated-filter]').forEach((element) => element.remove());
      mobileMain.insertAdjacentHTML(
        'afterbegin',
        this.filters.map((filter, index) => this.renderMobileFilter(filter, index)).join('')
      );
    }

    this.root.querySelectorAll('.mobile-facets__footer .mobile-facets__clear').forEach((link) => {
      if (!link.closest('[data-subcollection-generated-filter]')) {
        link.dataset.subcollectionClearAll = '';
        link.href = this.urlWithoutAllFilters();
      }
    });
  }

  renderDesktopFilter(filter, index) {
    const selectedCount = this.selectedValues(filter.id).length;
    const vertical = this.config.filterType === 'vertical';
    const detailsClass = vertical ? 'facets__disclosure-vertical' : 'disclosure-has-popup facets__disclosure';
    const displayClass = vertical ? 'facets__display-vertical' : 'facets__display';
    const open = vertical && index === 0 ? ' open' : '';

    return `
      <details
        id="SubcollectionDetails-${index}-${this.escapeAttribute(this.config.sectionId)}"
        class="${detailsClass} js-filter"
        data-index="${index + 1}"
        data-subcollection-generated-filter
        ${open}
      >
        <summary class="facets__summary caption-large focus-offset">
          <div>
            <span>${this.escapeHtml(filter.label)}</span>
            <span class="facets__selected${selectedCount ? '' : ' hidden'}">(${selectedCount})</span>
            ${this.icons.caret || ''}
          </div>
        </summary>
        <div class="${displayClass}">
          ${this.renderFilterBody(filter, 'desktop', index)}
        </div>
      </details>
    `;
  }

  renderMobileFilter(filter, index) {
    return `
      <details
        id="SubcollectionDetails-Mobile-${index}-${this.escapeAttribute(this.config.sectionId)}"
        class="mobile-facets__details js-filter"
        data-index="mobile-${index + 1}"
        data-subcollection-generated-filter
      >
        <summary class="mobile-facets__summary focus-inset">
          <div>
            <span>${this.escapeHtml(filter.label)}</span>
            <span class="mobile-facets__arrow no-js-hidden">${this.icons.arrow || ''}</span>
          </div>
        </summary>
        <div class="mobile-facets__submenu gradient">
          <button class="mobile-facets__close-button link link--text focus-inset" type="button" data-subcollection-mobile-back>
            ${this.icons.arrow || ''}
            ${this.escapeHtml(filter.label)}
          </button>
          ${this.renderFilterBody(filter, 'mobile', index)}
        </div>
      </details>
    `;
  }

  renderFilterBody(filter, context, index) {
    if (filter.type === 'PRICE_RANGE') {
      return this.renderPriceFilter(filter, context, index);
    }

    const selectedCount = this.selectedValues(filter.id).length;
    const items = filter.values
      .map((value, valueIndex) => this.renderFilterValue(filter, value, context, index, valueIndex))
      .join('');
    const listClass = context === 'mobile' ? 'mobile-facets__list list-unstyled' : 'facets__list list-unstyled';
    const footerClass = context === 'mobile' ? 'mobile-facets__footer gradient' : 'facets__header';

    const footer = `
      <div class="${footerClass}">
        <span class="${context === 'mobile' ? 'mobile-facets__count' : 'facets__selected'}">
          ${this.selectedCountLabel(selectedCount)}
        </span>
        <button
          type="button"
          class="${context === 'mobile' ? 'mobile-facets__clear' : 'facets__reset'} link underlined-link"
          data-subcollection-clear-filter="${this.escapeAttribute(filter.id)}"
        >
          ${this.escapeHtml(this.config.labels.reset)}
        </button>
      </div>
    `;
    const values = `
      <fieldset class="facets-wrap parent-wrap">
        <legend class="visually-hidden">${this.escapeHtml(filter.label)}</legend>
        <ul class="${listClass}" role="list">${items}</ul>
      </fieldset>
    `;

    return context === 'mobile' ? values + footer : footer + values;
  }

  renderFilterValue(filter, value, context, filterIndex, valueIndex) {
    const parameterValue = this.parameterValue(filter.id, value);
    const selected = this.hasParameterValue(filter.id, parameterValue);
    const disabled = value.count === 0 && !selected;
    const inputId = `SubcollectionFilter-${context}-${filterIndex}-${valueIndex}`;
    const itemClass = context === 'mobile' ? 'mobile-facets__item list-menu__item' : 'list-menu__item facets__item';

    return `
      <li class="${itemClass}">
        <label for="${inputId}" class="${context === 'mobile' ? 'mobile-facets__label' : 'facet-checkbox'}${disabled ? ' disabled' : ''}">
          <input
            type="checkbox"
            id="${inputId}"
            name="${this.escapeAttribute(filter.id)}"
            value="${this.escapeAttribute(parameterValue)}"
            ${selected ? 'checked' : ''}
            ${disabled ? 'disabled' : ''}
          >
          <svg width="1.6rem" height="1.6rem" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
            <rect width="16" height="16" stroke="currentColor" fill="none" stroke-width="1"></rect>
          </svg>
          <svg class="icon icon-checkmark" width="1.1rem" height="0.7rem" viewBox="0 0 11 7" fill="none" aria-hidden="true">
            <path d="M1.5 3.5 4.166 6 9.5 1" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"></path>
          </svg>
          <span aria-hidden="true">${this.escapeHtml(value.label)} (${value.count})</span>
          <span class="visually-hidden">${this.escapeHtml(value.label)} (${value.count})</span>
        </label>
      </li>
    `;
  }

  renderPriceFilter(filter, context, index) {
    const priceInput = this.parseFilterInput(filter.values?.[0]?.input);
    const maximum = priceInput?.price?.max || 0;
    const minimumValue = this.params.get('filter.v.price.gte') || '';
    const maximumValue = this.params.get('filter.v.price.lte') || '';
    const prefix = context === 'mobile' ? 'Mobile-' : '';

    return `
      <p class="${context === 'mobile' ? 'mobile-facets__info' : 'facets__info'}">
        ${this.escapeHtml(this.config.labels.maxPrice.replace('__PRICE__', `${this.config.currencySymbol}${maximum}`))}
      </p>
      <price-range class="facets__price">
        <span class="field-currency">${this.escapeHtml(this.config.currencySymbol)}</span>
        <div class="field">
          <input
            class="field__input"
            name="filter.v.price.gte"
            id="${prefix}SubcollectionPrice-${index}-GTE"
            type="number"
            inputmode="decimal"
            min="0"
            max="${maximum}"
            placeholder="0"
            value="${this.escapeAttribute(minimumValue)}"
          >
          <label class="field__label" for="${prefix}SubcollectionPrice-${index}-GTE">${this.escapeHtml(this.config.labels.from)}</label>
        </div>
        <span class="field-currency">${this.escapeHtml(this.config.currencySymbol)}</span>
        <div class="field">
          <input
            class="field__input"
            name="filter.v.price.lte"
            id="${prefix}SubcollectionPrice-${index}-LTE"
            type="number"
            inputmode="decimal"
            min="0"
            max="${maximum}"
            placeholder="${maximum}"
            value="${this.escapeAttribute(maximumValue)}"
          >
          <label class="field__label" for="${prefix}SubcollectionPrice-${index}-LTE">${this.escapeHtml(this.config.labels.to)}</label>
        </div>
      </price-range>
      <div class="${context === 'mobile' ? 'mobile-facets__footer' : 'facets__header'}">
        <button
          type="button"
          class="${context === 'mobile' ? 'mobile-facets__clear' : 'facets__reset'} link underlined-link"
          data-subcollection-clear-filter="filter.v.price"
        >
          ${this.escapeHtml(this.config.labels.reset)}
        </button>
      </div>
    `;
  }

  renderActiveFacets() {
    const activeFilters = [];
    const handledPrice = new Set();

    this.params.forEach((value, key) => {
      if (!key.startsWith('filter.')) return;
      if (key.startsWith('filter.v.price.')) {
        if (handledPrice.has('price')) return;
        handledPrice.add('price');
        const minimum = this.params.get('filter.v.price.gte') || '0';
        const maximum = this.params.get('filter.v.price.lte') || '';
        const label = maximum
          ? `${this.config.currencySymbol}${minimum} - ${this.config.currencySymbol}${maximum}`
          : `${this.config.currencySymbol}${minimum}+`;
        activeFilters.push({ key: 'filter.v.price', value: '', label });
        return;
      }

      const filter = this.filters.find((item) => item.id === key);
      const filterValue = filter?.values?.find((item) => this.parameterValue(key, item) === value);
      const label = filter
        ? `${filter.label}: ${filterValue?.label || value}`
        : value;
      activeFilters.push({ key, value, label });
    });

    const chips = activeFilters
      .map(
        (filter) => `
          <facet-remove>
            <a
              href="${this.escapeAttribute(this.urlWithoutFilter(filter.key, filter.value))}"
              class="active-facets__button active-facets__button--light"
              role="button"
              data-subcollection-remove-filter="${this.escapeAttribute(filter.key)}"
              data-subcollection-remove-value="${this.escapeAttribute(filter.value)}"
            >
              <span class="active-facets__button-inner button button--tertiary">
                ${this.escapeHtml(filter.label)}
                ${this.icons.close || ''}
                <span class="visually-hidden">${this.escapeHtml(this.config.labels.clearFilter)}</span>
              </span>
            </a>
          </facet-remove>
        `
      )
      .join('');

    const clearAll = activeFilters.length
      ? `
        <facet-remove class="active-facets__button-wrapper">
          <a
            href="${this.escapeAttribute(this.urlWithoutAllFilters())}"
            class="active-facets__button-remove underlined-link"
            role="button"
            data-subcollection-clear-all
          >
            <span>${this.escapeHtml(this.config.labels.clearAll)}</span>
          </a>
        </facet-remove>
      `
      : '';

    this.root.querySelectorAll('.active-facets-desktop, .active-facets-mobile').forEach((container) => {
      container.innerHTML = chips + clearAll;
    });
  }

  renderProductCount(count) {
    const template = count === 1 ? this.config.labels.productCountOne : this.config.labels.productCountOther;
    const label = template.replace('__COUNT__', String(count));
    this.root.querySelectorAll('#ProductCount, #ProductCountDesktop').forEach((element) => {
      element.textContent = label;
    });
  }

  async renderProducts(handles) {
    const missingHandles = handles.filter((handle) => !this.cardCache.has(handle));
    if (missingHandles.length) {
      await this.fetchCards(missingHandles);
    }

    const container = this.root.querySelector('#ProductGridContainer');
    if (!container) return;

    if (!handles.length) {
      container.innerHTML = `
        <div class="collection collection--empty page-width">
          <div class="loading-overlay gradient"></div>
          <div class="title-wrapper center">
            <h2 class="title title--primary">${this.escapeHtml(this.config.labels.empty)}</h2>
          </div>
        </div>
      `;
      return;
    }

    let grid = container.querySelector('#product-grid');
    if (!grid) {
      container.innerHTML = `
        <div id="ajax-product-grid" class="collection page-width">
          <ul
            id="product-grid"
            data-id="${this.escapeAttribute(this.config.sectionId)}"
            class="grid product-grid grid--${this.config.columnsMobile}-col-tablet-down grid--${this.config.columnsDesktop}-col-desktop"
          ></ul>
        </div>
      `;
      grid = container.querySelector('#product-grid');
    }

    grid.innerHTML = handles.map((handle) => this.cardCache.get(handle) || '').join('');
  }

  async fetchCards(handles) {
    for (let index = 0; index < handles.length; index += 20) {
      await this.fetchCardBatch(handles.slice(index, index + 20));
    }
  }

  async fetchCardBatch(handles) {
    const flags = [
      this.config.showSecondaryImage,
      this.config.showVendor,
      this.config.showRating,
      this.config.enableQuickAdd,
    ].map((value) => (value ? '1' : '0'));
    const payload = [
      handles.join(','),
      this.config.imageRatio,
      this.config.imageShape,
      ...flags,
      this.config.sectionId,
    ].join('~');
    const rootUrl = this.config.routesRoot.endsWith('/') ? this.config.routesRoot : `${this.config.routesRoot}/`;
    const url = `${rootUrl}search?view=subcollection-cards&type=product&q=${encodeURIComponent(payload)}`;
    const response = await fetch(url, { credentials: 'same-origin' });

    if (!response.ok) {
      throw new Error(`Product cards returned ${response.status}`);
    }

    const html = await response.text();
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    documentFragment.querySelectorAll('[data-subcollection-product-handle]').forEach((card) => {
      this.cardCache.set(card.dataset.subcollectionProductHandle, card.outerHTML);
    });

    const unresolved = handles.filter((handle) => !this.cardCache.has(handle));
    if (unresolved.length) {
      throw new Error(`Unable to render product cards: ${unresolved.join(', ')}`);
    }
  }

  renderPagination(totalPages, currentPage) {
    const container = this.root.querySelector('#ProductGridContainer');
    if (!container) return;
    container.querySelector('.pagination-wrapper')?.remove();
    if (totalPages <= 1) return;

    const items = [];
    if (currentPage > 1) {
      items.push(this.paginationLink(currentPage - 1, this.config.labels.previous, 'pagination__item--next pagination__item-arrow', this.icons.caret));
    }

    for (let page = 1; page <= totalPages; page += 1) {
      if (page === currentPage) {
        items.push(`
          <li>
            <a role="link" aria-disabled="true" class="pagination__item pagination__item--current light" aria-current="page">
              ${page}
            </a>
          </li>
        `);
      } else {
        items.push(this.paginationLink(page, this.config.labels.page.replace('__PAGE__', String(page)), '', String(page)));
      }
    }

    if (currentPage < totalPages) {
      items.push(this.paginationLink(currentPage + 1, this.config.labels.next, 'pagination__item--prev pagination__item-arrow', this.icons.caret));
    }

    container.insertAdjacentHTML(
      'beforeend',
      `
        <div class="pagination-wrapper">
          <nav class="pagination" role="navigation">
            <ul class="pagination__list list-unstyled" role="list">${items.join('')}</ul>
          </nav>
        </div>
      `
    );
  }

  paginationLink(page, label, extraClass, content) {
    const parameters = new URLSearchParams(this.params);
    if (page === 1) parameters.delete('page');
    else parameters.set('page', String(page));
    return `
      <li>
        <a
          href="${this.escapeAttribute(this.urlFor(parameters))}"
          class="pagination__item link motion-reduce ${extraClass}"
          aria-label="${this.escapeAttribute(label)}"
          data-subcollection-page="${page}"
        >
          ${content}
        </a>
      </li>
    `;
  }

  onChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;

    if (target.name === 'sort_by') {
      if (target.value) this.params.set('sort_by', target.value);
      else this.params.delete('sort_by');
      this.params.delete('page');
      this.load();
      return;
    }

    if (target.type === 'checkbox' && target.name.startsWith('filter.')) {
      this.toggleParameterValue(target.name, target.value, target.checked);
      this.params.delete('page');
      this.load();
    }
  }

  onInput(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.name.startsWith('filter.v.price.')) return;

    if (target.value) this.params.set(target.name, target.value);
    else this.params.delete(target.name);
    window.clearTimeout(this.priceTimer);
    this.priceTimer = window.setTimeout(() => {
      this.params.delete('page');
      this.load();
    }, 500);
  }

  onClick(event) {
    const pageLink = event.target.closest('[data-subcollection-page]');
    if (pageLink) {
      event.preventDefault();
      const page = Number.parseInt(pageLink.dataset.subcollectionPage, 10);
      if (page === 1) this.params.delete('page');
      else this.params.set('page', String(page));
      this.load({ scrollToGrid: true });
      return;
    }

    const clearAll = event.target.closest('[data-subcollection-clear-all]');
    if (clearAll) {
      event.preventDefault();
      [...this.params.keys()].forEach((key) => {
        if (key.startsWith('filter.')) this.params.delete(key);
      });
      this.params.delete('page');
      this.load();
      return;
    }

    const removeFilter = event.target.closest('[data-subcollection-remove-filter]');
    if (removeFilter) {
      event.preventDefault();
      const key = removeFilter.dataset.subcollectionRemoveFilter;
      const value = removeFilter.dataset.subcollectionRemoveValue;
      if (key === 'filter.v.price') {
        this.params.delete('filter.v.price.gte');
        this.params.delete('filter.v.price.lte');
      } else {
        this.toggleParameterValue(key, value, false);
      }
      this.params.delete('page');
      this.load();
      return;
    }

    const clearFilter = event.target.closest('[data-subcollection-clear-filter]');
    if (clearFilter) {
      event.preventDefault();
      const key = clearFilter.dataset.subcollectionClearFilter;
      [...this.params.keys()].forEach((parameter) => {
        if (parameter === key || parameter.startsWith(`${key}.`)) this.params.delete(parameter);
      });
      this.params.delete('page');
      this.load();
      return;
    }

    const mobileBack = event.target.closest('[data-subcollection-mobile-back]');
    if (mobileBack) {
      event.preventDefault();
      mobileBack.closest('details')?.removeAttribute('open');
    }
  }

  toggleParameterValue(key, value, enabled) {
    const values = this.params.getAll(key).filter((current) => current !== value);
    if (enabled) values.push(value);
    this.params.delete(key);
    values.forEach((current) => this.params.append(key, current));
  }

  selectedValues(filterId) {
    if (filterId === 'filter.v.price') {
      return ['filter.v.price.gte', 'filter.v.price.lte'].filter((key) => this.params.has(key));
    }
    return this.params.getAll(filterId);
  }

  hasParameterValue(key, value) {
    return this.params.getAll(key).includes(value);
  }

  parameterValue(filterId, value) {
    const input = this.parseFilterInput(value.input);
    if (filterId === 'filter.v.availability') return input?.available ? '1' : '0';
    if (input?.variantOption?.value != null) return String(input.variantOption.value);
    if (input?.productVendor != null) return String(input.productVendor);
    if (input?.productType != null) return String(input.productType);
    if (input?.tag != null) return String(input.tag);
    if (input?.productMetafield?.value != null) return String(input.productMetafield.value);
    if (input?.variantMetafield?.value != null) return String(input.variantMetafield.value);
    if (input?.category?.id != null) return String(input.category.id);
    return value.label;
  }

  parseFilterInput(input) {
    try {
      return JSON.parse(input || '{}');
    } catch {
      return {};
    }
  }

  selectedCountLabel(count) {
    const template = count === 1 ? this.config.labels.selectedOne : this.config.labels.selectedOther;
    return this.escapeHtml(template.replace('__COUNT__', String(count)));
  }

  syncSortSelects() {
    const value = this.params.get('sort_by') || this.config.defaultSort;
    this.root.querySelectorAll('select[name="sort_by"]').forEach((select) => {
      select.value = value;
    });
  }

  setLoading(loading) {
    this.root.classList.toggle('subcollection-facets--loading', loading);
    this.root.querySelectorAll('.loading__spinner').forEach((spinner) => spinner.classList.toggle('hidden', !loading));
    this.root.querySelector('#ProductGridContainer .collection')?.classList.toggle('loading', loading);
  }

  updateHistory(replace) {
    const url = this.urlFor(this.params);
    const state = { subcollectionSearch: this.params.toString() };
    if (replace) window.history.replaceState(state, '', url);
    else window.history.pushState(state, '', url);
  }

  urlWithoutFilter(key, value) {
    const parameters = new URLSearchParams(this.params);
    if (key === 'filter.v.price') {
      parameters.delete('filter.v.price.gte');
      parameters.delete('filter.v.price.lte');
    } else {
      const values = parameters.getAll(key).filter((current) => current !== value);
      parameters.delete(key);
      values.forEach((current) => parameters.append(key, current));
    }
    parameters.delete('page');
    return this.urlFor(parameters);
  }

  urlWithoutAllFilters() {
    const parameters = new URLSearchParams(this.params);
    [...parameters.keys()].forEach((key) => {
      if (key.startsWith('filter.')) parameters.delete(key);
    });
    parameters.delete('page');
    return this.urlFor(parameters);
  }

  urlFor(parameters) {
    const query = parameters.toString();
    return `${window.location.pathname}${query ? `?${query}` : ''}`;
  }

  escapeHtml(value) {
    const element = document.createElement('div');
    element.textContent = value == null ? '' : String(value);
    return element.innerHTML;
  }

  escapeAttribute(value) {
    return this.escapeHtml(value).replace(/"/g, '&quot;');
  }
}

SubcollectionFacets.query = `
  query SubcollectionProducts(
    $id: ID!
    $filters: [ProductFilter!]
    $sortKey: ProductCollectionSortKeys!
    $reverse: Boolean!
    $after: String
    $country: CountryCode
    $language: LanguageCode
  ) @inContext(country: $country, language: $language) {
    collection(id: $id) {
      products(
        first: 250
        after: $after
        filters: $filters
        sortKey: $sortKey
        reverse: $reverse
      ) {
        nodes {
          handle
        }
        pageInfo {
          hasNextPage
          endCursor
        }
        filters {
          id
          label
          type
          presentation
          values {
            id
            label
            count
            input
            swatch {
              color
              image {
                image {
                  url
                }
              }
            }
          }
        }
      }
    }
  }
`;

document.querySelectorAll('[data-subcollection-facets-root]').forEach((root) => {
  new SubcollectionFacets(root);
});
